import { DayShift, DayTemplate, DayTemplateShift } from '@/types';
import { format, set } from 'date-fns';

/**
 * Convert DayShift objects to DayTemplateShift objects for saving as template
 */
export const convertShiftsToTemplate = (
  dayShifts: DayShift[],
  saveEmployeeAssignments: boolean = false
): DayTemplateShift[] => {
  return dayShifts
    .filter(shift => !shift.isHistorical) // Exclude historical shifts
    .map(shift => ({
      startTime: format(shift.startTime, 'HH:mm'),
      endTime: format(shift.endTime, 'HH:mm'),
      title: shift.title || 'Shift',
      employeeRole: undefined,
      employeeId: saveEmployeeAssignments ? shift.employeeId : undefined,
      employeeName: saveEmployeeAssignments ? shift.employeeName : undefined,
      breaks: shift.breaks || [],
    }));
};

/**
 * Apply a day template to a specific date, creating new DayShift objects
 */
export const applyTemplateToDate = (template: DayTemplate, targetDate: Date): DayShift[] => {
  return template.shifts.map((templateShift, index) => {
    const [startHour, startMinute] = templateShift.startTime.split(':').map(Number);
    const [endHour, endMinute] = templateShift.endTime.split(':').map(Number);

    const startTime = set(new Date(targetDate), {
      hours: startHour,
      minutes: startMinute,
      seconds: 0,
      milliseconds: 0
    });

    const endTime = set(new Date(targetDate), {
      hours: endHour,
      minutes: endMinute,
      seconds: 0,
      milliseconds: 0
    });

    // Handle shifts that cross midnight
    if (endTime <= startTime) {
      endTime.setDate(endTime.getDate() + 1);
    }

    return {
      id: `template-${template.id}-${index}-${Date.now()}`, // Temporary ID for new shifts
      employeeName: templateShift.employeeName || '',
      employeeId: templateShift.employeeId || undefined,
      startTime,
      endTime,
      title: templateShift.title,
      breaks: templateShift.breaks,
      isNew: true,
    };
  });
};

/**
 * Calculate the total duration of a day template in minutes
 */
export const calculateTemplateDuration = (template: DayTemplate): number => {
  if (template.shifts.length === 0) return 0;
  
  let totalMinutes = 0;
  
  template.shifts.forEach(shift => {
    const [startHour, startMinute] = shift.startTime.split(':').map(Number);
    const [endHour, endMinute] = shift.endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMinute;
    let endMinutes = endHour * 60 + endMinute;
    
    // Handle shifts that cross midnight
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60; // Add 24 hours
    }
    
    totalMinutes += (endMinutes - startMinutes);
  });
  
  return totalMinutes;
};

/**
 * Get the time span (earliest start to latest end) of a day template
 */
export const getTemplateTimeSpan = (template: DayTemplate): { startMinutes: number; endMinutes: number; spanMinutes: number } => {
  if (template.shifts.length === 0) {
    return { startMinutes: 0, endMinutes: 0, spanMinutes: 0 };
  }
  
  let earliestMinutes = Infinity;
  let latestMinutes = 0;
  
  template.shifts.forEach(shift => {
    const [startHour, startMinute] = shift.startTime.split(':').map(Number);
    const [endHour, endMinute] = shift.endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMinute;
    let endMinutes = endHour * 60 + endMinute;
    
    // Handle shifts that cross midnight
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60;
    }
    
    earliestMinutes = Math.min(earliestMinutes, startMinutes);
    latestMinutes = Math.max(latestMinutes, endMinutes);
  });
  
  return {
    startMinutes: earliestMinutes,
    endMinutes: latestMinutes,
    spanMinutes: latestMinutes - earliestMinutes
  };
};

/**
 * Format duration in minutes to human-readable string
 */
export const formatDuration = (minutes: number): string => {
  if (minutes === 0) return '0m';
  
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  
  if (hours === 0) {
    return `${mins}m`;
  } else if (mins === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${mins}m`;
  }
};

/**
 * Validate if a day template is valid
 */
export const validateDayTemplate = (template: Partial<DayTemplate>): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!template.name?.trim()) {
    errors.push('Template name is required');
  }
  
  if (!template.shifts || template.shifts.length === 0) {
    errors.push('Template must contain at least one shift');
  }
  
  if (template.shifts) {
    template.shifts.forEach((shift, index) => {
      if (!shift.startTime || !shift.endTime) {
        errors.push(`Shift ${index + 1}: Start and end times are required`);
      }
      
      if (!shift.title?.trim()) {
        errors.push(`Shift ${index + 1}: Title is required`);
      }
      
      // Validate time format
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (shift.startTime && !timeRegex.test(shift.startTime)) {
        errors.push(`Shift ${index + 1}: Invalid start time format (use HH:MM)`);
      }
      
      if (shift.endTime && !timeRegex.test(shift.endTime)) {
        errors.push(`Shift ${index + 1}: Invalid end time format (use HH:MM)`);
      }
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Sort day template shifts by start time
 */
export const sortTemplateShifts = (shifts: DayTemplateShift[]): DayTemplateShift[] => {
  return [...shifts].sort((a, b) => {
    const [aHour, aMinute] = a.startTime.split(':').map(Number);
    const [bHour, bMinute] = b.startTime.split(':').map(Number);
    
    const aMinutes = aHour * 60 + aMinute;
    const bMinutes = bHour * 60 + bMinute;
    
    return aMinutes - bMinutes;
  });
};

/**
 * Check if two day templates have conflicting shifts when applied to the same day
 */
export const checkTemplateConflicts = (template1: DayTemplate, template2: DayTemplate): boolean => {
  const shifts1 = template1.shifts;
  const shifts2 = template2.shifts;
  
  for (const shift1 of shifts1) {
    for (const shift2 of shifts2) {
      const [start1Hour, start1Minute] = shift1.startTime.split(':').map(Number);
      const [end1Hour, end1Minute] = shift1.endTime.split(':').map(Number);
      const [start2Hour, start2Minute] = shift2.startTime.split(':').map(Number);
      const [end2Hour, end2Minute] = shift2.endTime.split(':').map(Number);
      
      const start1Minutes = start1Hour * 60 + start1Minute;
      let end1Minutes = end1Hour * 60 + end1Minute;
      const start2Minutes = start2Hour * 60 + start2Minute;
      let end2Minutes = end2Hour * 60 + end2Minute;
      
      // Handle midnight crossings
      if (end1Minutes <= start1Minutes) end1Minutes += 24 * 60;
      if (end2Minutes <= start2Minutes) end2Minutes += 24 * 60;
      
      // Check for overlap
      if (start1Minutes < end2Minutes && start2Minutes < end1Minutes) {
        return true; // Conflict found
      }
    }
  }
  
  return false; // No conflicts
};